/**
 * §9.7 step 1 — the coding rules, listed and edited.
 *
 * WHAT THIS SCREEN IS NOT FOR
 *
 * It is not where rules get made. The editor is: an operator codes an entry,
 * nothing yet codes that counterparty, and it offers to remember the mapping
 * on the spot. That is the same lesson the party aliases produced one feature
 * earlier, and the handoff states it flatly -- a settings screen nobody visits
 * collects nothing, and the operator goes on coding by hand forever.
 *
 * So this screen exists for the things the capture point cannot do: see every
 * rule at once, find the one that is wrong, re-point it, change which rule
 * wins, and retire one without losing the record that it existed.
 *
 * THE MATCH COUNT IS THE POINT OF THE TABLE
 *
 * A rule matching zero headers is the commonest mistake in a rules list, and a
 * pattern typed with a typo is indistinguishable from one that is simply
 * waiting for its vendor to appear. The count is computed on every read rather
 * than stored, because a stored one would be wrong after the next sync and
 * would be wrong in the reassuring direction.
 *
 * PRIORITY IS SHOWN AS A NUMBER, AND LOWER WINS
 *
 * That is genuinely ambiguous and silently wrong if read backwards, so the
 * column says so in its header rather than relying on the reader's convention.
 * Overlapping patterns are the reason it exists: "AWS" and "AWS BILLING" both
 * match "AWS BILLING INC" on purpose, and priority is how the specific one
 * wins.
 *
 * DEACTIVATE RATHER THAN DELETE, USUALLY
 *
 * Both are offered and they mean different things. Deactivating stops a rule
 * suggesting anything while keeping the record that it existed, which is also
 * the only way to change a rule's match type -- `contains` to `regex` keeps
 * the pattern and silently changes what it matches, so the API refuses the
 * edit and the operator writes a new rule instead. The partial unique index
 * from migration 0022 is what lets the replacement share the old pattern.
 */

import { projectWord, useBusiness, useCan, useUsesFunds } from '../auth/AuthProvider.js';
import { useCallback, useEffect, useState } from 'react';
import { Toast } from '../components/Toast.js';
import { Combobox } from '../components/Combobox.js';
import { matchTypeLabel } from '../lib/labels.js';
import {
  ApiError,
  api,
  type Account,
  type CodingRule,
  type Fund,
  type MatchType,
  type Phase,
  type Project,
} from '../api.js';

/** The new-rule form, which is deliberately small. */
interface RuleDraft {
  matchType: MatchType;
  pattern: string;
  accountId: string;
  priority: string;
  /** 0081 (§C.6): the fund and project the rule's entry goes in; empty for the defaults. */
  fundId: string;
  projectId: string;
  /** 0091: the phase the categorized line goes in; empty for none. */
  phaseId: string;
}

const emptyDraft: RuleDraft = {
  matchType: 'contains',
  pattern: '',
  accountId: '',
  priority: '',
  fundId: '',
  projectId: '',
  phaseId: '',
};

export function RulesPage() {
  // What this screen offers (0069); the API refuses the rest either way.
  const can = useCan();
  const mayEdit = can('edit_entries');
  const [rules, setRules] = useState<CodingRule[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [includeInactive, setIncludeInactive] = useState(false);
  /** 0081: the funds and projects a rule may name, when the business has them. */
  const usesFunds = useUsesFunds();
  const business = useBusiness();
  const word = projectWord(business);
  const [funds, setFunds] = useState<Fund[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [phases, setPhases] = useState<Phase[]>([]);

  const [draft, setDraft] = useState<RuleDraft>(emptyDraft);
  const [editing, setEditing] = useState<string | null>(null);

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const message = (caught: unknown, fallback: string) =>
    caught instanceof ApiError ? caught.message : fallback;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ruleResult, accountResult, projectResult, fundResult, phaseResult] = await Promise.all([
        api.listCodingRules(includeInactive),
        api.listAccounts(),
        api.listProjects(),
        usesFunds ? api.listFunds() : Promise.resolve({ usesFunds: false, funds: [] as Fund[] }),
        api.listPhases(),
      ]);
      setRules(ruleResult.rules);
      setAccounts(accountResult.accounts.filter((account) => account.isActive));
      setProjects(projectResult.projects.filter((project) => project.archivedAt === null));
      setFunds(fundResult.funds.filter((fund) => fund.archivedAt === null));
      setPhases(phaseResult.phases.filter((phase) => phase.archivedAt === null));
      setError(null);
    } catch (caught) {
      setError(message(caught, 'Could not load the rules.'));
    } finally {
      setLoading(false);
    }
  }, [includeInactive, usesFunds]);

  useEffect(() => {
    void load();
  }, [load]);

  async function create(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const priority = draft.priority.trim();
      await api.createCodingRule({
        matchType: draft.matchType,
        pattern: draft.pattern.trim(),
        accountId: draft.accountId,
        ...(priority !== '' && { priority: Number(priority) }),
        ...(draft.fundId !== '' && { fundId: draft.fundId }),
        ...(draft.projectId !== '' && carries(draft.accountId) && { projectId: draft.projectId }),
        ...(draft.phaseId !== '' && carriesPhase(draft.accountId) && { phaseId: draft.phaseId }),
      });
      setDraft(emptyDraft);
      setNotice('Rule added.');
      await load();
    } catch (caught) {
      // The server's sentence names the account a duplicate pattern already
      // codes to, which is what tells the operator which rule to go and edit.
      setError(message(caught, 'Could not add that rule.'));
    } finally {
      setBusy(false);
    }
  }

  /** Whether an account's lines may carry a project (§C.2). */
  const carries = (accountId: string) => {
    const account = accounts.find((candidate) => candidate.id === accountId);
    return account !== undefined && (account.type === 'revenue' || account.type === 'expense' || (account.type === 'asset' && account.tracksProjects === true));
  };
  /** 0091: a phase goes where a project may, never on a line holding stock. */
  const carriesPhase = (accountId: string) =>
    carries(accountId) && accounts.find((candidate) => candidate.id === accountId)?.inventoryRole !== 'stock';
  const showTags = usesFunds || projects.length > 0 || phases.length > 0;
  /**
   * "GRT · Tutoring": what a rule's entry goes in, or the defaults. A fund or
   * program archived since is marked, since the entries it categorizes are then
   * refused until the rule names one in use (review PR-3).
   */
  const goesIn = (rule: CodingRule) =>
    [
      rule.fundCode == null || rule.fundCode === '' ? null : `${rule.fundCode}${rule.fundArchived ? ' (Archived)' : ''}`,
      rule.projectName == null || rule.projectName === '' ? null : `${rule.projectName}${rule.projectArchived ? ' (Archived)' : ''}`,
      rule.phaseName == null || rule.phaseName === '' ? null : `${rule.phaseName}${rule.phaseArchived ? ' (Archived)' : ''}`,
    ]
      .filter((part) => part !== null)
      .join(' · ') || 'Defaults';

  async function patch(rule: CodingRule, change: Parameters<typeof api.updateCodingRule>[1]) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.updateCodingRule(rule.id, change);
      setEditing(null);
      await load();
    } catch (caught) {
      setError(message(caught, 'Could not change that rule.'));
    } finally {
      setBusy(false);
    }
  }

  async function remove(rule: CodingRule) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.deleteCodingRule(rule.id);
      setNotice(`Deleted the rule for “${rule.pattern}”.`);
      await load();
    } catch (caught) {
      setError(message(caught, 'Could not delete that rule.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="rules">
      <header className="row triage-head">
        <h1>Categorization Rules</h1>
        <span className="muted">
          {loading ? 'Loading…' : `${rules.length} rule${rules.length === 1 ? '' : 's'}`}
        </span>
      </header>

      {notice && (
        <Toast message={notice} onDismiss={() => setNotice(null)} />
      )}
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}

      {/*
        Says where rules come from, because an empty table with an "Add" form
        under it reads as an instruction to fill it in by hand -- which is the
        workflow this feature exists to replace.
      */}
      <p className="muted">
        A rule suggests an account for a counterparty. It never applies itself:
        the editor shows which rule matched and you accept it. Most rules are
        easier to make there, while categorizing an entry.
      </p>

      <div className="row triage-filters">
        <label>
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(event) => setIncludeInactive(event.target.checked)}
          />{' '}
          Show deactivated rules
        </label>
      </div>

      {rules.length === 0 && !loading ? (
        <p className="queue-empty muted">
          No rules yet. Categorize an entry and the editor will offer to remember it.
        </p>
      ) : (
        <table className="lines">
          <thead>
            <tr>
              <th>Pattern</th>
              <th>Match</th>
              <th>Account</th>
              {showTags && <th>Goes In</th>}
              <th className="num">Priority, lower wins</th>
              <th className="num">Matches now</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rules.map((rule) => (
              <tr key={rule.id} className={rule.isActive ? undefined : 'muted'}>
                <td>{rule.pattern}</td>
                {/* Title case for the operator; the API value is
                    unchanged and is what the select below sends. */}
                <td>{matchTypeLabel(rule.matchType)}</td>
                <td>
                  {editing === rule.id ? (
                    <Combobox
                      label="Account"
                      value={rule.accountId}
                      options={accounts.map((account) => ({
                        id: account.id,
                        label: `${account.code} ${account.name}`,
                      }))}
                      onChange={(accountId) => void patch(rule, { accountId })}
                    />
                  ) : mayEdit ? (
                    <button
                      type="button"
                      className="link"
                      onClick={() => setEditing(rule.id)}
                      disabled={busy}
                    >
                      {rule.accountCode} {rule.accountName}
                    </button>
                  ) : (
                    <>
                      {rule.accountCode} {rule.accountName}
                    </>
                  )}
                </td>
                {showTags && <td>{goesIn(rule)}</td>}
                <td className="num">
                  <input
                    type="number"
                    aria-label={`Priority for ${rule.pattern}`}
                    className="priority"
                    min={1}
                    max={1000}
                    defaultValue={rule.priority}
                    disabled={busy}
                    onBlur={(event) => {
                      const next = Number(event.target.value);
                      if (next !== rule.priority && next >= 1 && next <= 1000) {
                        void patch(rule, { priority: next });
                      }
                    }}
                  />
                </td>
                {/*
                  Zero is called out rather than shown as a bare 0. A rule
                  matching nothing is usually a typo, and a 0 in a column of
                  small numbers does not read as a problem.
                */}
                <td className="num">
                  {rule.matchCount === 0 ? (
                    <span title="This rule matches no entry today.">none</span>
                  ) : (
                    rule.matchCount
                  )}
                </td>
                <td>
                  {mayEdit && (
                  <>
                  <button
                    type="button"
                    className="link"
                    disabled={busy}
                    onClick={() => void patch(rule, { isActive: !rule.isActive })}
                  >
                    {rule.isActive ? 'Deactivate' : 'Reactivate'}
                  </button>{' '}
                  <button
                    type="button"
                    className="link"
                    disabled={busy}
                    onClick={() => void remove(rule)}
                  >
                    Delete
                  </button>
                  </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {mayEdit && (
        <form className="new-rule" onSubmit={create}>
          <h2>Add a rule</h2>
          <div className="row triage-filters">
            <label>
              Match{' '}
              <select
                aria-label="Match type"
                value={draft.matchType}
                onChange={(event) =>
                  setDraft({ ...draft, matchType: event.target.value as MatchType })
                }
              >
                {/* The VALUE is the API's, the label is the operator's. */}
                <option value="contains">Contains</option>
                <option value="regex">Regex</option>
              </select>
            </label>
            <label>
              Pattern{' '}
              <input
                aria-label="Pattern"
                value={draft.pattern}
                placeholder="AWS"
                onChange={(event) => setDraft({ ...draft, pattern: event.target.value })}
              />
            </label>
            <label className="rule-account">
              <span>Account</span>
              <Combobox
                label="Rule account"
                value={draft.accountId}
                options={accounts.map((account) => ({
                  id: account.id,
                  label: `${account.code} ${account.name}`,
                }))}
                placeholder="Search accounts…"
                onChange={(accountId) => setDraft({ ...draft, accountId })}
              />
            </label>
            {usesFunds && (
              <label>
                Fund{' '}
                <select
                  aria-label="Rule fund"
                  value={draft.fundId}
                  onChange={(event) => {
                    const fundId = event.target.value;
                    setDraft((current) => ({ ...current, fundId }));
                  }}
                >
                  <option value="">Default</option>
                  {funds.map((fund) => (
                    <option key={fund.id} value={fund.id}>
                      {fund.code} {fund.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {projects.length > 0 && carries(draft.accountId) && (
              <label>
                {word}{' '}
                <select
                  aria-label={`Rule ${word.toLowerCase()}`}
                  value={draft.projectId}
                  onChange={(event) => {
                    const projectId = event.target.value;
                    setDraft((current) => ({ ...current, projectId }));
                  }}
                >
                  <option value="">Default</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.path ?? project.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {phases.length > 0 && carriesPhase(draft.accountId) && (
              <label>
                Phase{' '}
                <select
                  aria-label="Rule phase"
                  value={draft.phaseId}
                  onChange={(event) => {
                    const phaseId = event.target.value;
                    setDraft((current) => ({ ...current, phaseId }));
                  }}
                >
                  <option value="">None</option>
                  {phases.map((phase) => (
                    <option key={phase.id} value={phase.id}>
                      {phase.path ?? phase.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label>
              Priority{' '}
              <input
                type="number"
                aria-label="Priority"
                className="priority"
                min={1}
                max={1000}
                placeholder="100"
                value={draft.priority}
                onChange={(event) => setDraft({ ...draft, priority: event.target.value })}
              />
            </label>
            <button
              type="submit"
              disabled={busy || draft.pattern.trim() === '' || draft.accountId === ''}
            >
              Add
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
